// ═══════════════════════════════════════════════════════════════════════
// EstimatedLostRevenue.tsx — Phase 5-C "operational drag" card
// ═══════════════════════════════════════════════════════════════════════
// Surfaces stated_annual_revenue as a credible drag estimate tied to the
// number of pain points the Architect captured. Order-of-magnitude
// signal — not a precise forecast. Renders nothing when revenue is
// missing or non-positive (no fabricated data).
// ═══════════════════════════════════════════════════════════════════════

interface EstimatedLostRevenueProps {
  revenue?: { value: number; currency: string } | null;
  painCount?: number;
}

// Sliding-scale drag percentage by pain count. Hand-tuned to feel
// believable rather than alarmist; arbitrary baseline that future work
// can calibrate against real benchmarks.
function dragPercentage(painCount: number): number {
  if (painCount <= 1) return 3;
  if (painCount === 2) return 6;
  return 9; // 3+ captured pains
}

function formatCurrency(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // Unknown currency code → fall back to a plain number with the code suffix
    return `${currency} ${Math.round(value).toLocaleString()}`;
  }
}

export function EstimatedLostRevenue({ revenue, painCount }: EstimatedLostRevenueProps) {
  if (!revenue || typeof revenue.value !== "number" || revenue.value <= 0) return null;
  const safePainCount = typeof painCount === "number" && painCount > 0 ? painCount : 1;
  const pct = dragPercentage(safePainCount);
  const drag = (revenue.value * pct) / 100;
  const plural = safePainCount === 1 ? "" : "s";

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-6">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-3">
        Estimated operational drag
      </h2>
      <div className="text-4xl font-semibold text-red-700 leading-none mb-3">
        {formatCurrency(drag, revenue.currency)}
      </div>
      <p className="text-sm text-zinc-700 leading-relaxed mb-2">
        ~{pct}% of your stated {revenue.currency} annual revenue ({formatCurrency(revenue.value, revenue.currency)}).
        Calibrated to the {safePainCount} bottleneck{plural} you described.
      </p>
      <p className="text-xs italic text-zinc-500 leading-relaxed">
        An order-of-magnitude estimate — sharpens as your Agentic Operating System captures real signal.
      </p>
    </section>
  );
}
